/**
 * qty-keyboard.ts — стрелки ↑/↓ на степпере количества и восстановление фокуса после DOM-patch.
 */

const QTY_ACTIONS = new Set([
  'change-qty',
  'increase-qty',
  'decrease-qty',
  'add-to-cart',
]);

export interface QtyFocusSnapshot {
  trigger: HTMLElement;
  action: string;
  delta: string | null;
  article: string | null;
  productId: string | null;
  scopeRoot: Element | Document;
}

function resolveScopeRoot(trigger: HTMLElement): Element | Document {
  return (
    trigger.closest('.product-card') ??
    trigger.closest('.list-row') ??
    trigger.closest('.product-page-actions') ??
    trigger.closest('.modal-cart-actions') ??
    trigger.closest('.modal-dialog') ??
    trigger.closest('#cart-items') ??
    trigger.closest('#korzina-wrapper') ??
    document
  );
}

function readRowIds(trigger: HTMLElement): { article: string | null; productId: string | null } {
  const row = trigger.closest('[data-article], [data-product-id]') as HTMLElement | null;
  return {
    article: row?.dataset.article?.trim() || trigger.dataset.article?.trim() || null,
    productId: row?.dataset.productId?.trim() || trigger.dataset.productId?.trim() || null,
  };
}

export function captureQtyFocus(trigger: HTMLElement): QtyFocusSnapshot | null {
  const action = trigger.dataset.action ?? '';
  if (!QTY_ACTIONS.has(action)) return null;
  const { article, productId } = readRowIds(trigger);
  return {
    trigger,
    action,
    delta: trigger.dataset.delta ?? null,
    article,
    productId,
    scopeRoot: resolveScopeRoot(trigger),
  };
}

function queryScope(scopeRoot: Element | Document, article: string | null, productId: string | null): Element | Document {
  if (scopeRoot !== document && (scopeRoot as Element).isConnected) {
    return scopeRoot;
  }
  if (article) {
    const el = document.querySelector(`[data-article="${CSS.escape(article)}"]`);
    if (el) return el;
  }
  if (productId) {
    const el = document.querySelector(`[data-product-id="${CSS.escape(productId)}"]`);
    if (el) return el;
  }
  return document;
}

function findQtyButton(
  root: Element | Document,
  kind: 'minus' | 'plus' | 'add',
): HTMLElement | null {
  const scope = root === document ? document : root;
  const q = (sel: string): HTMLElement | null => scope.querySelector(sel) as HTMLElement | null;

  if (kind === 'add') {
    return q('[data-action="add-to-cart"]');
  }
  if (kind === 'minus') {
    return (
      q('[data-action="change-qty"][data-delta="-1"]') ??
      q('[data-action="decrease-qty"]')
    );
  }
  return (
    q('[data-action="change-qty"][data-delta="1"]:not([disabled])') ??
    q('[data-action="increase-qty"]:not([disabled])')
  );
}

export function restoreQtyFocusAfterDomUpdate(snapshot: QtyFocusSnapshot | null): void {
  if (!snapshot) return;
  if (snapshot.trigger.isConnected) return;

  const root = queryScope(snapshot.scopeRoot, snapshot.article, snapshot.productId);

  let target: HTMLElement | null = null;
  const { action, delta } = snapshot;

  if (action === 'add-to-cart') {
    target = findQtyButton(root, 'plus');
  } else if (action === 'increase-qty' || (action === 'change-qty' && delta === '1')) {
    target = findQtyButton(root, 'plus');
  } else if (action === 'decrease-qty' || (action === 'change-qty' && delta === '-1')) {
    const minus = findQtyButton(root, 'minus');
    const add = findQtyButton(root, 'add');
    target = minus ?? add;
  }

  target?.focus();
}

export function runWithQtyFocusRestore(trigger: HTMLElement, fn: () => void): void {
  const snapshot = captureQtyFocus(trigger);
  fn();
  queueMicrotask(() => {
    restoreQtyFocusAfterDomUpdate(snapshot);
  });
}

let qtyKeyboardBound = false;

export function initQtyKeyboard(): void {
  if (qtyKeyboardBound) return;
  qtyKeyboardBound = true;

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const active = document.activeElement as HTMLElement | null;
    if (!active?.closest('.qty-controls')) return;

    event.preventDefault();
    const controls = active.closest('.qty-controls')!;
    const up = event.key === 'ArrowUp';
    const btn = up
      ? (controls.querySelector(
          '[data-action="change-qty"][data-delta="1"]:not([disabled]), [data-action="increase-qty"]:not([disabled])',
        ) as HTMLButtonElement | null)
      : (controls.querySelector(
          '[data-action="change-qty"][data-delta="-1"], [data-action="decrease-qty"]',
        ) as HTMLButtonElement | null);

    btn?.click();
  });
}
